import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { MESSAGES } from "@/constants";
import { usePublishContext as usePublishMutation } from "@/hooks/useContextConsole";
import {
  apiErrorMessage,
  type PickedContextFiles,
  type PublishContextOptions,
  type PublishResponse,
} from "@/services/apis/contextConsoleAPI";
import { useCompletionToast, useResetOnIdentityChange } from "./providerHooks";

const NOTIFY = MESSAGES.CONTEXT_CONSOLE.NOTIFY.PUBLISH;
const FAILED = MESSAGES.CONTEXT_CONSOLE.EVALUATE;

interface PublishVars extends PublishContextOptions {
  files: PickedContextFiles;
}

interface PublishContextValue {
  /** The last publish's receipt — `null` until one succeeds. Read straight
   * off the mutation (not a page-local copy), so it survives navigating away
   * mid-request and back: see `runPublish`'s docstring for the bug this
   * closes. */
  published: PublishResponse | null;
  isPendingPublish: boolean;
  /** The last attempt's raw error, straight off the mutation — a caller
   * reacts to THIS (e.g. re-deriving a gate-fail 409's scores), not a
   * per-call `onError`, for the same navigate-away-mid-request reason
   * `published` is read off the mutation instead of local state: see
   * `runPublish`'s docstring. */
  error: unknown;
  /** Same contract as FileEvaluationContext's `hasBeenViewed`: true once the
   * admin has actually seen this publish's result (inline, or via the
   * notification's "View receipt"), not just because the toast was
   * dismissed. */
  hasBeenViewedPublish: boolean;
  markViewedPublish: () => void;
  /** True once the off-page toast has already told the admin about the
   * CURRENT `error` — a caller with its own on-page failure toast (see
   * usePublishFlow.ts) checks this so a failure that landed while the admin
   * was elsewhere doesn't toast a second time once they're back. Cleared by
   * `resetPublish`. */
  hasNotifiedOffPage: boolean;
  /** Claims the once-per-publish "administrative" work (the notice dialog,
   * the adoption event, clearing a now-stale reason/gate-fail) for `setId`:
   * returns true the first time it's called for that set, false after. Lives
   * here, not as a page-local ref, so it survives a remount — `published`
   * itself doesn't change on navigate-away-and-back (it's read straight off
   * this context's own mutation), so a page-local marker re-initializes on
   * every fresh mount and would re-run those side effects for a publish the
   * admin already saw. Ref-backed and check-and-set in one step (not state),
   * so StrictMode's double-run of a mount effect can't slip both calls past
   * the check before a state update lands. */
  claimPublish: (setId: string) => boolean;
  /** The files, reason and options the last `runPublish` call sent, read off
   * the mutation — `null` before any attempt or after `resetPublish`. The
   * override-gate confirm replays this, since the page-local reason and
   * staged files are gone if the gate 409 landed after a remount. */
  lastPublishRequest: PublishVars | null;
  runPublish: (vars: PublishVars) => void;
  resetPublish: () => void;
}

const PublishContext = createContext<PublishContextValue | undefined>(undefined);

/**
 * Returns a function that notifies the admin a publish settled while they were
 * on another page. Deliberately does NOT mark the receipt viewed: that would
 * flip `hasBeenViewedPublish` true before ContextConsole.tsx has even mounted,
 * so its own resume-check (`published && !hasBeenViewedPublish`) would read
 * false on its very first render and never land on Complete — the page marks
 * it viewed itself, once it's actually there to act on it.
 */
const usePublishNotification = () => {
  const showCompletionToast = useCompletionToast();
  return useCallback(
    (result: PublishResponse | null, failMessage: string | null): boolean =>
      showCompletionToast({
        toastId: "context-publish",
        success: result !== null,
        title: result ? NOTIFY.TITLE : FAILED.PUBLISH_FAILED_TITLE,
        description: result ? NOTIFY.BODY : failMessage || FAILED.PUBLISH_FAILED_BODY,
        viewLabel: NOTIFY.VIEW,
      }),
    [showCompletionToast],
  );
};

/**
 * The once-per-publish claim (see `claimPublish` on the context value): a ref,
 * checked and set in one step. `clear` forgets it, so the next publish — even
 * of the same set id — is treated as new.
 */
const useHandledPublish = () => {
  const handledSetIdRef = useRef<string | null>(null);
  const claim = useCallback((setId: string) => {
    if (handledSetIdRef.current === setId) return false;
    handledSetIdRef.current = setId;
    return true;
  }, []);
  const clear = useCallback(() => {
    handledSetIdRef.current = null;
  }, []);
  return { claim, clear };
};

/**
 * Tracks whether the off-page completion toast already told the admin about
 * the CURRENT failure, so a page effect with its own on-page failure toast
 * (see usePublishFlow.ts) can skip it rather than repeat the same message.
 */
const useOffPageNotified = () => {
  const [hasNotifiedOffPage, setHasNotifiedOffPage] = useState(false);
  const reset = useCallback(() => setHasNotifiedOffPage(false), []);
  return { hasNotifiedOffPage, setHasNotifiedOffPage, reset };
};

/**
 * Fires the publish mutation. No per-call onSuccess/onError: the caller
 * reacts to `published`/`error` (both read straight off this same mutation)
 * instead, in an effect keyed on those values rather than a callback closed
 * over whichever page instance happened to call this — see the context's own
 * docstring and `published`'s doc comment for the navigate-away bug that
 * caused. The notifications are deliberately generic even for a
 * below-threshold gate 409: the override-confirm dialog is page-local and
 * doesn't survive a navigate-away either.
 */
const useRunPublish = (
  mutate: ReturnType<typeof usePublishMutation>["mutate"],
  notify: ReturnType<typeof usePublishNotification>,
  setHasBeenViewedPublish: (viewed: boolean) => void,
  offPage: ReturnType<typeof useOffPageNotified>,
) =>
  useCallback(
    (vars: PublishVars) => {
      setHasBeenViewedPublish(false);
      offPage.reset();
      mutate(vars, {
        onSuccess: (result) => notify(result, null),
        onError: (err) => offPage.setHasNotifiedOffPage(notify(null, apiErrorMessage(err))),
      });
    },
    [mutate, notify, setHasBeenViewedPublish, offPage],
  );

/**
 * Lifts the publish mutation above the router — mirrors FileEvaluationContext
 * (same reasoning): a publish is real request time (longer still if the
 * company's publish lock is briefly contended), and a page-local
 * `usePublishContext()` would unmount if the admin navigates away first. The
 * in-flight promise's own `onSuccess`/`onError` still run either way (they're
 * attached to the mutation, not the component), but a FRESH mutation
 * instance on remount reads `isPending: false` / `data: undefined` as if
 * nothing happened — silently losing track of a publish that's still running
 * or already landed. Living here instead means every mount reads the SAME
 * mutation's live state.
 */
export const PublishProvider = ({
  children,
  identityKey = "anon",
}: {
  children: ReactNode;
  /** See EvaluationJobContext's identity-change effect — same reasoning: a
   * same-tab account switch must not leave one company's publish receipt
   * sitting there for the next signed-in admin. */
  identityKey?: string;
}) => {
  const mutation = usePublishMutation();
  const { mutate, reset: resetMutation } = mutation;
  const [hasBeenViewedPublish, setHasBeenViewedPublish] = useState(false);
  const { claim: claimPublish, clear: clearHandledPublish } = useHandledPublish();
  const offPage = useOffPageNotified();
  const notify = usePublishNotification();

  const markViewedPublish = useCallback(() => setHasBeenViewedPublish(true), []);
  const resetPublish = useCallback(() => {
    resetMutation();
    setHasBeenViewedPublish(false);
    clearHandledPublish();
    offPage.reset();
  }, [resetMutation, clearHandledPublish, offPage]);
  const runPublish = useRunPublish(mutate, notify, setHasBeenViewedPublish, offPage);

  useResetOnIdentityChange(identityKey, resetPublish);

  const published = mutation.data ?? null;
  const lastPublishRequest = mutation.variables ?? null;
  const value = useMemo(
    () => ({
      published,
      isPendingPublish: mutation.isPending,
      error: mutation.error,
      hasBeenViewedPublish,
      hasNotifiedOffPage: offPage.hasNotifiedOffPage,
      markViewedPublish,
      claimPublish,
      lastPublishRequest,
      runPublish,
      resetPublish,
    }),
    [
      published, mutation.isPending, mutation.error, hasBeenViewedPublish, offPage.hasNotifiedOffPage,
      markViewedPublish, claimPublish, lastPublishRequest, runPublish, resetPublish,
    ],
  );

  return <PublishContext.Provider value={value}>{children}</PublishContext.Provider>;
};

export const usePublish = (): PublishContextValue => {
  const context = useContext(PublishContext);
  if (context === undefined) {
    throw new Error("usePublish must be used within a PublishProvider");
  }
  return context;
};
